import type { PaymentRequest, Stripe } from '@stripe/stripe-js';
import { useCallback, useEffect, useState } from 'react';

const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
]);

export const usePaymentRequest = ({
  stripe,
  planInfo,
}: {
  stripe: Stripe | null;
  planInfo: {
    currency: string;
    fullPriceDiscount: number;
    planName: string;
    priceId: string;
  };
}) => {
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null,
  );
  const [paymentMethodResult, setPaymentMethodResult] = useState<
    string | null | undefined
  >(null);

  const toStripeAmount = useCallback((amount: number, currency: string) => {
    return ZERO_DECIMAL_CURRENCIES.has(currency.toLowerCase())
      ? Math.round(amount)
      : Math.round(amount * 100);
  }, []);

  const createPaymentRequest = useCallback(
    ({ ignore }: { ignore: boolean }) => {
      if (!stripe || planInfo.priceId.length === 0) {
        // We can't create a PaymentRequest until Stripe.js loads.
        return;
      }

      const pr = stripe.paymentRequest({
        country: 'CY',
        currency: planInfo.currency.toLowerCase(),
        total: {
          label: planInfo.planName,
          amount: toStripeAmount(planInfo.fullPriceDiscount, planInfo.currency),
        },
        disableWallets: ['link'],
        requestPayerName: false,
        requestPayerEmail: false,
      });

      // Check the availability of the Payment Request API.
      pr.canMakePayment().then((result: any) => {
        if (result && !ignore) {
          setPaymentRequest(pr);
          setPaymentMethodResult(
            Object.keys(result).find((method) => result[method]),
          );
        }
      });
    },
    [
      planInfo.currency,
      planInfo.fullPriceDiscount,
      planInfo.planName,
      planInfo.priceId.length,
      toStripeAmount,
      stripe,
    ],
  );

  useEffect(() => {
    if (!stripe) return; // We can't create a PaymentRequest until Stripe.js loads.

    const createRequest = () => {
      setPaymentMethodResult(null);
      setPaymentRequest(null);
      createPaymentRequest({ ignore: false });
    };

    createRequest();

    return () => {
      createPaymentRequest({ ignore: true });
    };
  }, [createPaymentRequest, stripe]);

  return {
    paymentRequest,
    paymentMethodResult,
  };
};
